import React, { useEffect, useRef, useState } from 'react';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import { Avatar, Box, ClickAwayListener, Popover, Typography } from '@material-ui/core';
import { userApiRef } from '../../../apis/userApi';
import { debounce } from 'lodash';
import {
  IUserProfileModal,
  userProfileApiRef,
} from '../../../apis/userProfileApi';
import { THEME_IDS } from '../../../App';

export interface IUserPopupProps {
  email: string | undefined;
}

const getInitials = (name?: string, email?: string) => {
  if (name && name.trim().length) {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join('');
  }
  if (email && email.includes('@')) {
    return email[0]?.toUpperCase() || '?';
  }
  return '?';
};

const UserPopup = () => {
  const [userState, setUserState] = useState<IUserProfileModal>();
  const [userDetails, setUserDetails] = useState<any>();
  const [userImage, setUserImage] = useState<string | undefined>();

  const appThemeApi = useApi(appThemeApiRef);
  const activeThemeId = appThemeApi.getActiveThemeId();

  const mousePosition = useRef({
    x: 0,
    y: 0,
  });

  const userProfileApi = useApi(userProfileApiRef);
  const graphUserApi = useApi(userApiRef);

  const onMouseMove = (event: any) => {
    const x = event?.clientX;
    const y = event?.clientY;
    mousePosition.current = { x, y };
  };

  const callback = debounce((value: IUserProfileModal) => {
    const anchorRef = value.anchorRef;
    if (anchorRef === undefined) {
      setUserDetails(undefined);
      setUserImage(undefined);
      setUserState(undefined);
      return;
    }
    const rect = anchorRef.getBoundingClientRect();

    const mouseXPosition = mousePosition.current.x;
    const mouseYPosition = mousePosition.current.y;

    // Check if the cursor remains on the originating element before opening the popup.
    if (
      rect.left <= mouseXPosition &&
      rect.left + rect.width >= mouseXPosition &&
      rect.top <= mouseYPosition &&
      rect.top + rect.height >= mouseYPosition
    ) {
      setUserState(value);
      setUserDetails(undefined);
      setUserImage(undefined);
      if (value.email) {
        graphUserApi
          .getDetails(value.email)
          .then(data => {
            setUserDetails(data || {});
          })
          .catch(() => {
            setUserDetails({});
          });

        graphUserApi
          .getImage(value.email)
          .then(image => {
            setUserImage(typeof image === 'string' ? image : undefined);
          })
          .catch(() => {
            setUserImage(undefined);
          });
      }
    } else {
      setUserDetails(undefined);
      setUserImage(undefined);
      setUserState(undefined);
    }
  }, 500);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    let subscription: { unsubscribe: any } | undefined = undefined;
    if (userProfileApi) {
      subscription = userProfileApi
        .publisher$()
        .subscribe((value: IUserProfileModal) => {
          callback(value);
        });
    }
    return () => {
      if (subscription !== undefined) {
        subscription.unsubscribe();
      }
      window.removeEventListener('mousemove', onMouseMove);
    };
  });

  const onClose = () => {
    setUserDetails(undefined);
    setUserImage(undefined);
    setUserState(undefined);
  };

  return (
    <>
      {userState !== undefined && userState.email !== undefined && (
        <Popover
          open={userState !== undefined && userState.email !== undefined}
          anchorEl={userState?.anchorRef}
          onMouseOut={onClose}
          onClose={onClose}
          BackdropProps={{
            style: {
              display: 'none',
            },
          }}
          hideBackdrop
        >
          <Box>
            <ClickAwayListener onClickAway={onClose}>
              <Box
                className={
                  activeThemeId === THEME_IDS.dark
                    ? 'mgt-dark custom-person-card'
                    : 'mgt-light custom-person-card'
                }
                p={2}
                minWidth={320}
              >
                <Box display="flex" alignItems="center" mb={1.5}>
                  <Avatar src={userImage}>
                    {!userImage
                      ? getInitials(userDetails?.displayName, userState?.email)
                      : null}
                  </Avatar>
                  <Box ml={1.5}>
                    <Typography variant="subtitle1">
                      {userDetails?.displayName || userState?.email}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {userState?.email}
                    </Typography>
                  </Box>
                </Box>

                {userDetails?.jobTitle ? (
                  <Typography variant="body2">{userDetails.jobTitle}</Typography>
                ) : null}
                {userDetails?.department ? (
                  <Typography variant="body2" color="textSecondary">
                    {userDetails.department}
                  </Typography>
                ) : null}
                {userDetails?.officeLocation ? (
                  <Typography variant="body2" color="textSecondary">
                    {userDetails.officeLocation}
                  </Typography>
                ) : null}
              </Box>
            </ClickAwayListener>
          </Box>
        </Popover>
      )}
    </>
  );
};

export default UserPopup;
